import {
  Button,
  DatePicker,
  Form,
  InputNumber,
  Select,
  Space,
  Switch,
} from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import AddIcon from "../../../../assets/icons/AddIcon";
import DateIcon from "../../../../assets/icons/DateIcon";
import DeleteIcon from "../../../../assets/icons/DeleteIcon";
import CustomModal from "../../../../components/ui/CustomModal";
import Loader from "../../../../components/ui/Loader";
import { employeeTypeList } from "../../../../constants/constants";
import { useGetDepartmentsQuery } from "../../../../features/department/departmentApi";
import { useGetDesignationsQuery } from "../../../../features/designation/designationApi";
import { useGetEmployeesQuery } from "../../../../features/employee/employeeApi";
import {
  useCreateOvertimeMutation,
  useGetOvertimeQuery,
  useGetPayrollSettingQuery,
  useUpdateOvertimeStatusMutation,
} from "../../../../features/payrollSetting/payrollSettingApi";
import useError from "../../../../hooks/useError";
import { toast } from "../../../../lib/toast";
import { ModalComponentProps } from "../../../../types";
import { selectArrayOption, selectSearchOption } from "../../../../utils/utils";
import { ListComponentType } from "./AdditionsModal";

export default function OvertimeModal({
  handleClose,
  data: componentItem,
  ...rest
}: ModalComponentProps<any>) {
  const [form] = Form.useForm();
  const { isLoading, isError, error, data } = useGetOvertimeQuery();
  const [isEnable, setIsEnable] = useState(false);
  const [
    createOvertime,
    {
      isLoading: isCreateLoading,
      isError: isCreateError,
      error: createError,
      isSuccess: isCreateSuccess,
    },
  ] = useCreateOvertimeMutation();

  const [
    updateOvertimeStatus,
    {
      isLoading: isStatusLoading,
      isError: isStatusError,
      error: statusError,
      isSuccess: isStatusSuccess,
    },
  ] = useUpdateOvertimeStatusMutation();

  useError(isError, error, "Get overtime setting data fail");
  useError(isCreateError, createError, "Fail to update overtime setting data");
  useError(isStatusError, statusError, "Fail to update status");

  const {
    data: salaryData,
    isLoading: isSalaryLoading,
    isError: isSalaryError,
    error: salaryError,
  } = useGetPayrollSettingQuery();
  useError(isSalaryError, salaryError, "Get salary breakdown data fail");

  const [sData, setSData] = useState<any>();

  useEffect(() => {
    if (salaryData) {
      setSData([
        ...(typeListCalculationSelect || []),
        ...(salaryData?.data?.payroll_setting?.salary_breakdown
          ?.filter((item) => item?.is_active)
          ?.map((item) => ({
            label: `% of ${item?.title}`,
            value: item?.key,
          })) || []),
      ]);
    }
  }, [salaryData]);

  const onFinish = (values: any) => {
    const overtime = values?.overtime?.map((item: any) => ({
      ...item,
      target: item[`target-${item["effective_for"]}`]?.toString() || null,
      schedule_month: item?.fixed_date_time
        ? item?.fixed_date_time?.month() + 1
        : null,
      schedule_year: item?.fixed_date_time
        ? item?.fixed_date_time?.year()
        : null,
      "target-department": undefined,
      "target-designation": undefined,
      "target-employee_type": undefined,
      "target-employee": undefined,
      fixed_date_time: undefined,
    }));

    createOvertime({ overtime });
  };

  useEffect(() => {
    if (data?.data?.results?.length) {
      const overtime = data?.data?.results?.map((item) => ({
        id: item?.id,
        on_what: item?.on_what,
        fixed_amount: item?.fixed_amount ?? undefined,
        effective_for: item?.effective_for ?? undefined,
        multiplier: Number(item?.multiplier) ?? undefined,
        schedule_type: item?.schedule_type ?? undefined,
        "target-department":
          item?.effective_for === "department"
            ? Number(item?.target)
            : undefined,
        "target-designation":
          item?.effective_for === "designation"
            ? Number(item?.target)
            : undefined,
        "target-employee_type":
          item?.effective_for === "employee_type" ? item?.target : undefined,
        "target-employee":
          item?.effective_for === "employee" ? Number(item?.target) : undefined,
        fixed_date_time:
          item?.schedule_type === "fixed_date" &&
          item?.schedule_month &&
          item?.schedule_year
            ? dayjs(`${item?.schedule_year}-${item?.schedule_month}-01`)
            : undefined,
      }));

      form.setFieldsValue({ overtime });
    }
  }, [data?.data?.results, form]);

  const handleChangeStatus = (checked: boolean) => {
    setIsEnable(checked);
    updateOvertimeStatus({
      component_id: componentItem?.id,
      is_enabled: checked,
    });
  };

  useEffect(() => {
    if (isCreateSuccess) {
      toast("success", "Overtime setting updated successfully");
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreateSuccess]);

  useEffect(() => {
    if (isStatusSuccess) {
      toast("success", "Status updated successfully");
    }
  }, [isStatusSuccess]);

  useEffect(() => {
    if (componentItem) {
      setIsEnable(componentItem?.is_active ? true : false);
    }
  }, [componentItem]);

  return (
    <CustomModal
      {...rest}
      title="Overtime Setting"
      handleClose={handleClose}
      maskClosable={false}
      footer={null}
      width={1100}
    >
      <div className={`flex items-center gap-2 my-4`}>
        <Switch
          checked={isEnable}
          onChange={handleChangeStatus}
          disabled={isStatusLoading}
        />

        <div className="text-header text-sm font-medium">
          <label className="cursor-pointer select-none">
            {isEnable ? "Enabled " : "Disabled "}
          </label>
        </div>
      </div>
      <div className={!isEnable ? "min-h-[100px]" : ""} />

      <div className={!isEnable ? "hidden" : ""}>
        <Form
          className="custom-form"
          layout="vertical"
          requiredMark="optional"
          size="large"
          onFinish={onFinish}
          form={form}
        >
          <div className="flex items-center justify-end mb-2">
            {/* <h3 className="text-header text-[22px] font-semibold">
              Tax Setting
            </h3> */}
            <label
              htmlFor="add-unpaid-leave"
              className="outline-button-primary border flex items-center py-1.5 px-2 cursor-pointer gap-1 rounded"
            >
              <AddIcon />
              <span>Add More</span>
            </label>
          </div>

          {isLoading ? (
            <Loader />
          ) : (
            <>
              <div>
                <Form.List initialValue={[undefined]} name="overtime">
                  {(fields, { add, remove }) => {
                    return (
                      <>
                        <button
                          className="sr-only"
                          id="add-unpaid-leave"
                          onClick={() => add()}
                          type="button"
                        >
                          Add
                        </button>
                        {fields?.map((field, index) => (
                          <div
                            className={`flex justify-between items-center flex-wrap pt-2 pb-2 px-3 py-1 bg-[#fbfcfd] border border-light attendance-table ${
                              index === 0 ? "border-t" : "border-t-0"
                            }`}
                            key={field.key}
                          >
                            <div>
                              <div className="sr-only">
                                <Form.Item
                                  className="mb-0"
                                  name={[index, "id"]}
                                />
                              </div>
                              <div className="flex items-center gap-2.5">
                                <span className="text-display-body text-sm font-medium">
                                  Effective For
                                </span>
                                <Form.Item
                                  validateFirst
                                  name={[index, "effective_for"]}
                                  className="mb-0"
                                  rules={[
                                    {
                                      required: true,
                                      message: "Please select",
                                    },
                                  ]}
                                  initialValue={"global"}
                                >
                                  <Select
                                    className="w-full"
                                    // suffixIcon={<SelectDropdownLgIcon />}
                                    options={effectiveForList}
                                    // loading={isSalaryLoading}
                                    style={{ width: 150 }}
                                    size="small"
                                    placeholder="Select"
                                  />
                                </Form.Item>
                                <EffectiveFor index={index} form={form} />

                                <span className="text-display-body text-sm font-medium">
                                  On What
                                </span>
                                <Form.Item
                                  validateFirst
                                  name={[index, "on_what"]}
                                  className="mb-0"
                                  rules={[
                                    {
                                      required: true,
                                      message: "Please select component",
                                    },
                                  ]}
                                  initialValue={"fixed"}
                                >
                                  <Select
                                    className="w-full"
                                    // suffixIcon={<SelectDropdownLgIcon />}
                                    options={sData}
                                    loading={isSalaryLoading}
                                    style={{ width: 170 }}
                                    popupMatchSelectWidth={180}
                                    size="small"
                                    placeholder="Select"
                                  />
                                </Form.Item>
                                <CalculateType index={index} form={form} />
                              </div>
                              <div className="flex items-center gap-2.5">
                                <span className="text-display-body text-sm font-medium">
                                  Schedule Type
                                </span>
                                <Form.Item
                                  validateFirst
                                  name={[index, "schedule_type"]}
                                  className="mb-0"
                                  rules={[
                                    {
                                      required: true,
                                      message: "Please select",
                                    },
                                  ]}
                                  initialValue={"fixed_date"}
                                >
                                  <Select
                                    className="w-full"
                                    // suffixIcon={<SelectDropdownLgIcon />}
                                    options={scheduleTypeList}
                                    // loading={isSalaryLoading}
                                    style={{ width: 130 }}
                                    popupMatchSelectWidth={180}
                                    size="small"
                                    placeholder="Select"
                                  />
                                </Form.Item>
                                <ScheduleType index={index} form={form} />
                              </div>
                            </div>

                            <Button
                              className="action-button"
                              size="small"
                              icon={<DeleteIcon />}
                              onClick={() => {
                                remove(field.name);
                              }}
                            />
                          </div>
                        ))}
                      </>
                    );
                  }}
                </Form.List>
              </div>
              <Space className="w-full pt-6 justify-end" size={12}>
                {/* <Button
            className="normal-dark-button modal-button"
            onClick={handleClose}
            size="middle"
          >
            Cancel
          </Button> */}

                {/* {isEnable && ( */}
                <Button
                  className="modal-button"
                  type="primary"
                  htmlType="submit"
                  size="middle"
                  loading={isCreateLoading}
                >
                  Save
                </Button>
                {/* )} */}
              </Space>
            </>
          )}
        </Form>
      </div>
    </CustomModal>
  );
}

const CalculateType = ({ index, form }: ListComponentType) => {
  const onWhat = Form.useWatch(["overtime", index, "on_what"], form);

  return onWhat === "fixed" ? (
    <>
      <span className="text-display-body text-sm font-medium">Amount</span>

      <Form.Item
        validateFirst
        name={[index, "fixed_amount"]}
        // label="Enter Amount"
        className="mb-0"
        rules={[
          {
            required: true,
            message: "Please enter amount",
          },
          {
            type: "number",
            min: 0,
            message: "Amount cannot be negative",
          },
          {
            validator: async (_: any, value: string) => {
              if (!value) {
                return Promise.resolve();
              }

              if (value?.toString().length > 9) {
                return Promise.reject(
                  new Error("Amount cannot exceed 9 digits")
                );
              }
            },
          },
        ]}
      >
        <InputNumber
          placeholder="0.00"
          type="number"
          //   className="w-full"
          prefix="BDT"
          onKeyDown={(e) => {
            if (e.key === "ArrowUp" || e.key === "ArrowDown") {
              e.preventDefault();
            }
          }}
          onWheel={(e) => {
            e.preventDefault();
            (e.target as HTMLElement).blur();
          }}
          keyboard={false}
          controls={false}
          style={{ width: 130 }}
          size="small"
        />
      </Form.Item>
    </>
  ) : (
    <>
      <span className="text-display-body text-sm font-medium">Multiplier</span>
      <Form.Item
        validateFirst
        name={[index, "multiplier"]}
        // label="Enter Percentage"
        className="mb-0"
        rules={[
          {
            required: true,
            message: "Please enter multiplier",
          },
          {
            type: "number",
            min: 0,
            message: "Multiplier cannot be negative",
          },
          // {
          //   type: "number",
          //   max: 100,
          //   message: "Percentage can not be more than 100%",
          // },
        ]}
      >
        <InputNumber
          placeholder="Value"
          type="number"
          // size="middle"
          className="w-full"
          onKeyDown={(e) => {
            if (e.key === "ArrowUp" || e.key === "ArrowDown") {
              e.preventDefault();
            }
          }}
          onWheel={(e) => {
            e.preventDefault();
            (e.target as HTMLElement).blur();
          }}
          controls={false}
          style={{ width: 100 }}
          // suffix={<PercentageIcon />}
          size="small"
        />
      </Form.Item>
    </>
  );
};

const EffectiveFor = ({ index, form }: ListComponentType) => {
  const effective = Form.useWatch(["overtime", index, "effective_for"], form);
  const {
    isLoading: isEmployeeLoading,
    isError: isEmployeeError,
    error: employeeError,
    data: employeeData,
  } = useGetEmployeesQuery({
    params: {
      status: "active",
      skip: 0,
      limit: 0,
    },
  });
  const {
    isLoading: isDepartmentLoading,
    isError: isDepartmentError,
    error: departmentError,
    data: departmentData,
  } = useGetDepartmentsQuery({
    params: { skip: 0, limit: 0, is_published: 1 },
  });
  const {
    isLoading: isDesignationLoading,
    data: designationData,
    isError: isDesignationError,
    error: designationError,
  } = useGetDesignationsQuery({
    params: {
      limit: 0,
      skip: 0,
      is_published: 1,
    },
  });
  useError(isEmployeeError, employeeError, "Get employee data fail");
  useError(isDepartmentError, departmentError, "Get department data fail");
  useError(isDesignationError, designationError, "Get designation fail");

  return effective === "employee_type" ? (
    <>
      <span className="text-display-body text-sm font-medium">Target</span>

      <Form.Item
        validateFirst
        name={[index, "target-employee_type"]}
        className="mb-0"
        rules={[
          {
            required: true,
            message: "Please select",
          },
        ]}
      >
        <Select
          className="w-full"
          // suffixIcon={<SelectDropdownLgIcon />}
          options={employeeTypeList}
          style={{ width: 140 }}
          popupMatchSelectWidth={180}
          size="small"
          placeholder="Select"
        />
      </Form.Item>
    </>
  ) : effective === "employee" ? (
    <>
      <span className="text-display-body text-sm font-medium">Target</span>

      <Form.Item
        validateFirst
        name={[index, "target-employee"]}
        className="mb-0"
        rules={[
          {
            required: true,
            message: "Please select",
          },
        ]}
      >
        <Select
          className="w-full"
          // suffixIcon={<SelectDropdownLgIcon />}
          options={employeeData?.data?.employees?.map((item) => ({
            label: item?.member?.profile?.name,
            value: item?.id,
          }))}
          loading={isEmployeeLoading}
          style={{ width: 160 }}
          popupMatchSelectWidth={250}
          size="small"
          placeholder="Select"
          filterOption={selectSearchOption}
          showSearch
        />
      </Form.Item>
    </>
  ) : effective === "department" ? (
    <>
      <span className="text-display-body text-sm font-medium">Target</span>

      <Form.Item
        validateFirst
        name={[index, "target-department"]}
        className="mb-0"
        rules={[
          {
            required: true,
            message: "Please select",
          },
        ]}
      >
        <Select
          className="w-full"
          // suffixIcon={<SelectDropdownLgIcon />}
          options={selectArrayOption(
            departmentData?.data?.departments || [],
            "name",
            "id"
          )}
          loading={isDepartmentLoading}
          style={{ width: 150 }}
          popupMatchSelectWidth={180}
          size="small"
          placeholder="Select"
          filterOption={selectSearchOption}
          showSearch
        />
      </Form.Item>
    </>
  ) : effective === "designation" ? (
    <>
      <span className="text-display-body text-sm font-medium ">Target</span>

      <Form.Item
        validateFirst
        name={[index, "target-designation"]}
        className="mb-0"
        rules={[
          {
            required: true,
            message: "Please select",
          },
        ]}
      >
        <Select
          className="w-full"
          // suffixIcon={<SelectDropdownLgIcon />}
          options={
            designationData?.data?.roles?.map((item) => ({
              label: `${item?.name} (${item?.department?.name})`,
              value: item?.id,
            })) || []
          }
          loading={isDesignationLoading}
          style={{ width: 190 }}
          popupMatchSelectWidth={250}
          size="small"
          placeholder="Select"
          filterOption={selectSearchOption}
          showSearch
        />
      </Form.Item>
    </>
  ) : null;
};

const ScheduleType = ({ index, form }: ListComponentType) => {
  const onWhat = Form.useWatch(["overtime", index, "schedule_type"], form);

  return onWhat === "fixed_date" ? (
    <>
      <span className="text-display-body text-sm font-medium">Schedule</span>

      <Form.Item
        validateFirst
        name={[index, "fixed_date_time"]}
        className="mb-0"
        rules={[
          {
            required: true,
            message: "Please Select",
          },
        ]}
      >
        <DatePicker
          format="MMM YYYY"
          suffixIcon={<DateIcon />}
          picker="month"
          size="small"
          className="clear-button-hide w-[130px]"
        />
      </Form.Item>
    </>
  ) : null;
};

// const dayTypes = [
//   {
//     label: "Holiday",
//     value: "holiday",
//   },
//   {
//     label: "Weekend",
//     value: "weekend",
//   },
//   {
//     label: "Normal",
//     value: "normal",
//   },
// ];

const scheduleTypeList = [
  {
    label: "Fixed Date",
    value: "fixed_date",
  },
  {
    label: "Periodically",
    value: "periodically",
  },
];

const typeListCalculationSelect = [{ label: "Fixed", value: "fixed" }];

const effectiveForList = [
  {
    label: "Global",
    value: "global",
  },
  {
    label: "Employee Type",
    value: "employee_type",
  },
  {
    label: "Employee",
    value: "employee",
  },
  {
    label: "Department",
    value: "department",
  },
  {
    label: "Designation",
    value: "designation",
  },
];
